/**
 * =============================================================================
 * RELEASE FLAGS (ADR-025 blueprint §3.5 — release-toggle contract, M17)
 * =============================================================================
 *
 * Tenant-scoped, server-evaluated release toggles for staged rollout of
 * replacement admin surfaces (Wave B: B-1 shell, B-4 workspace). This module
 * is the SINGLE SOURCE OF TRUTH for the flag registry and the fail-closed
 * resolver, shared by the worker (evaluation) and the admin UI (reading the
 * server's decision — the client NEVER decides a flag itself).
 *
 * CONTRACT (frozen — blueprint §3.5 / acceptance M17)
 * ---------------------------------------------------
 *   - SUBSTRATE: persisted per tenant on `stores.settings.releaseFlags`
 *     (JSON — runtime-mutable via a server-side data change, NO redeploy and
 *     NO new storage system). A Worker env var participates ONLY as a global
 *     kill-switch default (`RELEASE_FLAGS_KILL_SWITCH` — see the worker's
 *     `ReleaseFlagService`), never as the flag substrate.
 *   - DEFAULT OFF; FAIL CLOSED: a missing, malformed, or non-`true` persisted
 *     value resolves to `false`. Only the literal boolean `true` turns a flag
 *     on (mirrors `resolvePublicLaunchMode` / the strict-true
 *     `submissionAcknowledgementEnabled` semantics).
 *   - CONTROL-PLANE SEPARATION: release flags ≠ `publicLaunchMode` (public
 *     exposure) ≠ entitlements (ADR-019) ≠ capabilities (authorization).
 *     A release flag may NEVER gate a security control or tenancy: with every
 *     flag OFF, all auth/capability/tenant enforcement is byte-identical.
 *   - Exposure decisions are logged PII-safe at the evaluation point (worker).
 *
 * FLAG REGISTRY (each flag documents owner / purpose / expiry / removal
 * criteria per §3.5; a flag with no registry entry does not exist):
 *
 *   `adminWorkbenchShell`
 *     - Owner:    William Wright (product owner; ADR-025 ratifier).
 *     - Purpose:  mounts the B-1 `AdminShell` (semantic-location navigation +
 *       keep-alive sections) in place of the legacy single-scroll admin
 *       layout. OFF ⇒ the prior flat layout renders unchanged (the rollback
 *       surface, one flip away).
 *     - Expiry:   review/remove-by 2026-10-13 (a concrete date per §3.5; ~3
 *       months, the anticipated Wave B B-2..B-4 window). This is a REVIEW
 *       date, not a hard auto-removal: the actual trigger is the removal
 *       criteria below (event-anchored). If the pilot has not completed by
 *       this date, the owner extends it (recording the new date here) rather
 *       than removing the flag prematurely.
 *     - Removal criteria: P1 outcome + drill recorded in the evidence doc +
 *       owner sign-off → a dedicated flag-removal PR deletes the flag, the
 *       legacy layout fallback, and this registry entry, citing the evidence.
 *
 *   `adminQuestionWorkspace`
 *     - Owner:    William Wright (product owner; ADR-025 ratifier).
 *     - Purpose:  selects the B-4 adaptive, ID-keyed question workspace inside
 *       AdminShell. OFF keeps QuestionDetailModal as the immediate rollback
 *       surface; this flag is intentionally independent from the shell flag.
 *     - Expiry:   review/remove-by 2026-10-13.
 *     - Removal criteria: P2 staff-pilot outcome + recorded OFF→ON→OFF drill +
 *       owner sign-off → delete the flag and legacy detail modal in a
 *       dedicated retirement PR.
 * =============================================================================
 */

/** Registered release-flag names. UNREGISTERED names must never be read. */
export const RELEASE_FLAG_NAMES = [
  'adminWorkbenchShell',
  'adminQuestionWorkspace',
] as const;

export type ReleaseFlagName = (typeof RELEASE_FLAG_NAMES)[number];

/**
 * EFFECTIVE (server-evaluated) release-flag values. Every registered flag is
 * always present so consumers never distinguish "absent" from "off" — both
 * are `false` (fail-closed).
 */
export type ReleaseFlags = Record<ReleaseFlagName, boolean>;

/** Fail-closed default: every registered flag OFF. */
export const DEFAULT_RELEASE_FLAGS: Readonly<ReleaseFlags> = Object.freeze({
  adminWorkbenchShell: false,
  adminQuestionWorkspace: false,
});

/**
 * Fail-closed resolver for a PERSISTED (or wire-received) release-flags
 * value. Use this everywhere an untrusted value is read:
 *
 *   - non-object / null / array input → every flag `false`;
 *   - per flag, only the literal boolean `true` resolves ON — `"true"`, `1`,
 *     truthy objects, and absence all resolve `false`;
 *   - unregistered keys in the input are ignored (never echoed back).
 *
 * NOTE: this resolves the persisted TENANT value only. The worker applies the
 * global kill-switch on top of this (see `ReleaseFlagService`); the admin UI
 * applies this to the server response so a missing/malformed field from an
 * older worker fails closed to OFF rather than throwing.
 */
export function resolveReleaseFlags(value: unknown): ReleaseFlags {
  const source =
    value !== null && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : undefined;

  const resolved = {} as Record<ReleaseFlagName, boolean>;
  for (const name of RELEASE_FLAG_NAMES) {
    resolved[name] = source?.[name] === true;
  }
  return resolved;
}
